import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaTimes } from 'react-icons/fa';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import fileIcon from '../../assets/file.svg';
import downloadIcon from '../../assets/Download.svg';
import viewAssetIcon from '../../assets/Eye.svg';
import downloadAssetIcon from '../../assets/Download-grey.svg';
import api from '../../api';
import { notify } from '../CustomToast';
import calendarTaskService from '../../services/calendarTaskService';
import TaskAttachmentOnlyOfficeViewerModal from '../Projects/TaskAttachmentOnlyOfficeViewerModal';
import '../Projects/UploadedDocumentsModal.css';
import '../FileManager/PdfViewerModal.css';
import '../FileManager/ImageViewerModal.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

const PDF_EXTENSIONS = ['pdf'];
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
const OFFICE_EXTENSIONS = ['doc', 'docx', 'odt', 'rtf', 'xls', 'xlsx', 'ods', 'csv', 'ppt', 'pptx', 'odp'];

const getExt = (a) => (a.original_filename || a.name || '').split('.').pop()?.toLowerCase() || '';
const isPdf    = (a) => PDF_EXTENSIONS.includes(getExt(a));
const isImage  = (a) => IMAGE_EXTENSIONS.includes(getExt(a));
const isOffice = (a) => OFFICE_EXTENSIONS.includes(getExt(a));

const getFileIconColor = (ext) => {
    const colors = {
        'PDF': '#dc2626',
        'DOC': '#2b579a', 'DOCX': '#2b579a',
        'XLS': '#217346', 'XLSX': '#217346', 'CSV': '#217346',
        'JPG': '#e67e22', 'JPEG': '#e67e22', 'PNG': '#e67e22', 'SVG': '#e67e22', 'GIF': '#e67e22',
        'TXT': '#7f8c8d', 'RTF': '#7f8c8d',
        'ZIP': '#8e44ad', 'RAR': '#8e44ad', '7Z': '#8e44ad',
    };
    return colors[ext] || '#0c5097';
};

const FileIconBadge = ({ ext = 'FILE' }) => (
    <div className="file-type-icon">
        <div className="file-icon-base">
            <div className="file-icon-lines">
                <div className="file-icon-line" />
                <div className="file-icon-line" />
                <div className="file-icon-line" />
            </div>
            <span className="file-icon-pdf-badge" style={{ backgroundColor: getFileIconColor(ext) }}>
                {ext}
            </span>
        </div>
    </div>
);

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day   = String(d.getDate()).padStart(2, '0');
    const year  = d.getFullYear();
    let hours   = d.getHours();
    const mins  = String(d.getMinutes()).padStart(2, '0');
    const ampm  = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${month}-${day}-${year} ${hours}:${mins} ${ampm}`;
};

// ── Inline PDF viewer ─────────────────────────────────────────────────────────

const CalendarPdfViewer = ({ attachment, onClose }) => {
    const url = attachment.file_url;
    const title = attachment.original_filename || 'PDF Document';
    return (
        <div className="pdf-viewer-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="pdf-viewer-modal">
                <div className="pdf-viewer-header">
                    <h3 className="pdf-viewer-title">{title}</h3>
                    <button className="pdf-viewer-close" onClick={onClose}><FaTimes /></button>
                </div>
                <div className="pdf-viewer-content">
                    {url
                        ? <iframe src={url} className="pdf-viewer-iframe" title={title} />
                        : <div className="pdf-viewer-error"><p>File URL not available</p><button onClick={onClose}>Close</button></div>
                    }
                </div>
            </div>
        </div>
    );
};

// ── Inline Image viewer (lightbox) ────────────────────────────────────────────

const CalendarImageViewer = ({ attachment, allImageAttachments, onClose }) => {
    const imagesToShow = allImageAttachments.length > 0 ? allImageAttachments : [attachment];
    const slides = imagesToShow
        .filter(a => a.file_url)
        .map(a => ({ src: a.file_url, title: a.original_filename || 'Image', alt: a.original_filename || 'Image' }));

    const startIndex = Math.max(0, slides.findIndex(s => s.src === attachment.file_url));

    if (slides.length === 0) return null;

    return (
        <Lightbox
            open={true}
            close={onClose}
            slides={slides}
            index={startIndex}
            plugins={[Zoom]}
            animation={{ fade: 250, swipe: 250 }}
            carousel={{ finite: true }}
            controller={{ closeOnBackdropClick: true }}
            styles={{ container: { backgroundColor: 'rgba(0,0,0,0.95)' } }}
            zoom={{ maxZoomPixelRatio: 3, zoomInMultiplier: 2, doubleClickMaxStops: 2 }}
        />
    );
};

// ── CalendarTaskOnlyOfficeViewer ─────────────────────────────────────────────
// Wraps the existing TaskAttachmentOnlyOfficeViewerModal with a calendar service call

const CalendarOnlyOfficeViewer = ({ taskId, attachment, onClose }) => {
    // Adapt: pass a fake "projectService" via a service shim so the existing modal works
    // But it's easier to create a thin wrapper that feeds a custom getConfig function.
    // We do this by creating an object shaped like projectService.
    const calendarService = useMemo(() => ({
        getAttachmentOnlyOfficeConfig: (tId, attachmentId) =>
            api.get(`/calendar/tasks/${tId}/attachments/${attachmentId}/onlyoffice-config`),
    }), []);

    // The existing modal calls projectService.getAttachmentOnlyOfficeConfig(taskId, attachment.id)
    // We need to pass our shim. Since it's hardcoded, create an inline version here.
    return (
        <CalendarOnlyOfficeViewerInner
            taskId={taskId}
            attachment={attachment}
            onClose={onClose}
            getConfig={calendarService.getAttachmentOnlyOfficeConfig}
        />
    );
};

// Mirrors TaskAttachmentOnlyOfficeViewerModal but uses a passed getConfig fn
const detectDocumentType = (fileType) => {
    if (['doc','docx','odt','rtf'].includes(fileType)) return 'word';
    if (['xls','xlsx','ods','csv'].includes(fileType)) return 'cell';
    if (['ppt','pptx','odp'].includes(fileType)) return 'slide';
    return null;
};

const CalendarOnlyOfficeViewerInner = ({ taskId, attachment, onClose, getConfig }) => {
    const containerRef = useRef(null);
    const [errorMsg, setErrorMsg] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!taskId || !attachment?.id) { setLoading(false); setErrorMsg('No file provided'); return; }

        let editor = null;

        const init = async () => {
            try {
                const res = await getConfig(taskId, attachment.id);
                if (!res?.status || !res?.data) { setErrorMsg('Failed to load document configuration.'); setLoading(false); return; }

                const config  = res.data;
                const docType = detectDocumentType(config.fileType);
                if (!docType) { setErrorMsg(`Unsupported file type: .${config.fileType}`); setLoading(false); return; }

                const scriptSrc = `${window.ONLYOFFICE_SERVER_URL || ''}/web-apps/apps/api/documents/api.js`;
                await new Promise((resolve, reject) => {
                    if (window.DocsAPI) { resolve(); return; }
                    const script  = document.createElement('script');
                    script.src    = scriptSrc;
                    script.onload = resolve;
                    script.onerror = () => reject(new Error('Failed to load OnlyOffice DocsAPI script.'));
                    document.head.appendChild(script);
                });

                const isOwner = config.permissions?.is_owner || false;

                editor = new window.DocsAPI.DocEditor(containerRef.current, {
                    document: {
                        fileType  : config.fileType,
                        key       : config.key,
                        title     : config.title,
                        url       : config.documentUrl,
                        permissions: { edit: isOwner, download: true },
                    },
                    documentType: docType,
                    editorConfig: {
                        mode          : isOwner ? 'edit' : 'view',
                        callbackUrl   : config.callbackUrl || '',
                        customization : { autosave: true, forcesave: false },
                    },
                    height: '100%',
                    width : '100%',
                });
                setLoading(false);
            } catch (err) {
                setErrorMsg(err.message || 'Failed to load document viewer.');
                setLoading(false);
            }
        };

        init();
        return () => { try { editor?.destroyEditor?.(); } catch (_) {} };
    }, [taskId, attachment, getConfig]);

    return (
        <div style={{ position:'fixed', inset:0, zIndex:10000, background:'rgba(0,0,0,0.7)', display:'flex', flexDirection:'column' }}>
            <div style={{ display:'flex', justifyContent:'flex-end', padding:'8px 16px', background:'#1a1a2e' }}>
                <button onClick={onClose} style={{ background:'none', border:'none', color:'#fff', fontSize:20, cursor:'pointer' }}>✕</button>
            </div>
            {loading && <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff' }}>Loading document...</div>}
            {errorMsg && <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:'#f87171', flexDirection:'column', gap:12 }}><p>{errorMsg}</p><button onClick={onClose}>Close</button></div>}
            <div ref={containerRef} style={{ flex:1, display: loading || errorMsg ? 'none' : 'block' }} />
        </div>
    );
};

// ── Main Modal ────────────────────────────────────────────────────────────────

const CalendarTaskDocumentsModal = ({ isOpen, onClose, task, attachments: initialAttachments = [], onUpload }) => {
    const [attachments, setAttachments] = useState(initialAttachments);
    const [uploadingFiles, setUploadingFiles] = useState([]);
    const [isDownloadingAll, setIsDownloadingAll] = useState(false);
    const fileInputRef = useRef(null);

    const [pdfAttachment,    setPdfAttachment]    = useState(null);
    const [imageAttachment,  setImageAttachment]  = useState(null);
    const [officeAttachment, setOfficeAttachment] = useState(null);

    const imageAttachments = useMemo(
        () => attachments.filter(a => isImage(a)),
        [attachments]
    );

    useEffect(() => { setAttachments(initialAttachments); }, [initialAttachments]);

    if (!isOpen) return null;

    const handleBackdropClick = (e) => { if (e.target === e.currentTarget) onClose(); };

    const getExt = (a) => (a.original_filename || a.name || '').split('.').pop()?.toUpperCase() || 'FILE';

    const handleUploadClick = () => fileInputRef.current?.click();

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 50 * 1024 * 1024) { notify('error', 'File too large — max 50MB'); return; }

        const uploadId = Date.now();
        setUploadingFiles(prev => [{ id: uploadId, file_name: file.name, isUploading: true }, ...prev]);

        try {
            const formData = new FormData();
            formData.append('file', file);
            const tempRes = await api.post('/temp-upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

            if (tempRes?.status) {
                const tempId = tempRes.data.id;
                const updateRes = await calendarTaskService.updateTask(task.id, { attachment_ids: [tempId] });
                const updatedTask = updateRes?.data ?? updateRes;
                const newAttachments = updatedTask?.attachments || [];
                const newOne = newAttachments[newAttachments.length - 1] || {
                    id: tempId, original_filename: file.name, file_url: tempRes.data.file_url, created_at: new Date().toISOString(),
                };
                setAttachments(prev => [newOne, ...prev]);
                if (onUpload) onUpload(newOne);
                notify('success', 'File uploaded successfully');
            }
        } catch (err) {
            notify('error', err?.message || 'Upload failed');
        } finally {
            setUploadingFiles(prev => prev.filter(f => f.id !== uploadId));
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDownload = (attachment) => {
        const url = attachment.file_url;
        if (!url) { notify('error', 'File URL not available'); return; }
        const link = document.createElement('a');
        link.href = url;
        link.download = attachment.original_filename || 'document';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const handleView = (attachment) => {
        if (isPdf(attachment))    { setPdfAttachment(attachment); return; }
        if (isImage(attachment))  { setImageAttachment(attachment); return; }
        if (isOffice(attachment)) { setOfficeAttachment(attachment); return; }
        window.open(attachment.file_url, '_blank');
    };

    const anyViewerOpen = pdfAttachment || imageAttachment || officeAttachment;

    return (
        <>
        <div className="uploaded-docs-modal-overlay" onClick={handleBackdropClick} style={anyViewerOpen ? { display: 'none' } : undefined}>
            <div className="uploaded-docs-modal">
                <div className="uploaded-docs-modal-header">
                    <h2 className="uploaded-docs-modal-title">Uploaded Documents</h2>
                    <button className="uploaded-docs-modal-close" onClick={onClose}><FaTimes /></button>
                </div>

                <div className="uploaded-docs-modal-content">
                    {attachments.length === 0 && uploadingFiles.length === 0 ? (
                        <div className="uploaded-docs-empty">No documents uploaded yet</div>
                    ) : (
                        <>
                            {uploadingFiles.map(f => (
                                <div key={f.id} className="uploaded-doc-item uploading">
                                    <div className="uploaded-doc-item-main">
                                        <div className="uploaded-doc-info">
                                            <FileIconBadge ext={f.file_name.split('.').pop()?.toUpperCase() || 'FILE'} />
                                            <div className="uploaded-doc-details">
                                                <span className="uploaded-doc-name">{f.file_name}</span>
                                                <span className="upload-progress-text">Uploading...</span>
                                            </div>
                                        </div>
                                        <div className="uploaded-doc-actions"><div className="spinner-mini" /></div>
                                    </div>
                                </div>
                            ))}
                            {attachments.map(attachment => (
                                <div key={attachment.id} className="uploaded-doc-item">
                                    <div className="uploaded-doc-item-main">
                                        <div className="uploaded-doc-info">
                                            <FileIconBadge ext={getExt(attachment)} />
                                            <div className="uploaded-doc-details">
                                                <span className="uploaded-doc-name">
                                                    {attachment.original_filename || attachment.name || 'Document'}
                                                </span>
                                                <span className="uploaded-doc-date">{formatDate(attachment.created_at)}</span>
                                            </div>
                                        </div>
                                        <div className="uploaded-doc-actions">
                                            <button className="uploaded-doc-action-btn" onClick={() => handleView(attachment)} title="View">
                                                <img src={viewAssetIcon} alt="View" className="doc-action-icon" />
                                            </button>
                                            <button className="uploaded-doc-action-btn" onClick={() => handleDownload(attachment)} title="Download">
                                                <img src={downloadAssetIcon} alt="Download" className="doc-action-icon" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </div>

                <div className="uploaded-docs-modal-footer">
                    <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} />
                    <button className="upload-doc-btn" onClick={handleUploadClick}>
                        <img src={fileIcon} className="upload-doc-icon" alt="" />
                        <span>Upload Document</span>
                    </button>
                    <button
                        className="download-all-btn"
                        disabled={attachments.length === 0 || isDownloadingAll}
                        onClick={async () => {
                            setIsDownloadingAll(true);
                            try {
                                attachments.forEach(a => { if (a.file_url) window.open(a.file_url, '_blank'); });
                            } finally {
                                setIsDownloadingAll(false);
                            }
                        }}
                    >
                        <img src={downloadIcon} className="download-all-icon" alt="" />
                        <span>Download All</span>
                    </button>
                </div>
            </div>
        </div>

        {pdfAttachment && (
            <CalendarPdfViewer attachment={pdfAttachment} onClose={() => setPdfAttachment(null)} />
        )}
        {imageAttachment && (
            <CalendarImageViewer
                attachment={imageAttachment}
                allImageAttachments={imageAttachments}
                onClose={() => setImageAttachment(null)}
            />
        )}
        {officeAttachment && (
            <CalendarOnlyOfficeViewer
                taskId={task.id}
                attachment={officeAttachment}
                onClose={() => setOfficeAttachment(null)}
            />
        )}
        </>
    );
};

export default CalendarTaskDocumentsModal;
